// LoginWindow — modal-style auth chrome reused across signin / signup /
// reset-password pages. Hosts the email/password form (LoginForm /
// SignupForm / ResetEmailForm via slot replacement) plus the Google +
// GitHub OAuth buttons.
//
// OAuth path: routes through the SYMBOLS SDK (MongoDB-backed), same as
// the email/password path — `el.sdk.googleAuth(idToken)` and
// `el.sdk.githubAuth(code)`. Identical to the legacy editor behavior.
// We do NOT route through Supabase here; the federated Supabase
// session is minted later as a fan-out from the SDK login on
// email/password (see signin.js _fanOutBridgeLogin), and OAuth users
// pick up Supabase via their first authenticated workspace request
// hitting the bridge — not via the OAuth handshake itself.
//
// Google: Google Identity Services One Tap embedded inline. The
// gsi/client script is loaded by index.html so `window.google` is
// available; `onInit` initializes with the env client id and `onClick`
// triggers the One Tap prompt. Google calls back into our credential
// handler with an ID token, which we hand to `el.sdk.googleAuth`.
//
// GitHub: classic OAuth redirect. Click → store invite token in
// localStorage (since the OAuth round-trip strips arbitrary query
// params) → top-level navigate to GitHub. The provider redirects back
// to `/github-auth-callback?code=…` which calls `el.sdk.githubAuth`
// to mint the Symbols SDK session.
//
// Native shell (Tauri WKWebView, isNativeShell()): NO Google path can work
// embedded — FedCM/One Tap is unavailable in WKWebView, the shell drops the
// code-client's `window.open` popup, and Google policy rejects OAuth from
// embedded webviews outright ("disallowed_useragent"). So in-shell we skip
// ALL GIS wiring and instead run OAuth in the SYSTEM browser with a
// `symbols://auth-callback` deep link back (tickets/ipad.md): onRender
// arms the deep-link entry paths + feature-detects the server exchange
// route (ensureNativeGoogleAuth → s.nativeAuthReady), and onClick hands
// the redirect-flow URL to the system browser (launchNativeGoogleAuth).
// When the server predates the flow (probe 404s) the shipped guard stays:
// button disabled with the reason rendered inline (P_nativeShellNotice) —
// email/password + magic-link remain the fallback path. Every shell branch
// is gated on isNativeShell(); the web path is untouched when it returns
// false.

import { stashOAuthNext, consumeOAuthNext } from '../functions/postOAuthNext.js'
import { stashInviteToken, readInviteToken, clearInviteToken } from '../functions/inviteToken.js'

export const LoginWindow = {
  extends: 'ModalWindow',
  // FA204 / frank trap — `completeGoogleLogin` used to be a bare module-scope
  // async function read from inside two `onClick`/callback closures (a
  // serialized factory). It read as undefined once this component was
  // frank.toJSON'd for platform delivery (workspace d6ea1142,
  // project_frank_drops_module_local_helpers). Moved into `scope`, called via
  // `el.scope.completeGoogleLogin(...)`.
  scope: {
    // Shared post-credential flow for BOTH Google paths (One Tap id-token and
    // the explicit OAuth code-client): hydrate the SDK session, then route to
    // the post-OAuth `next`.
    async completeGoogleLogin(el, s, loginResult) {
      await el.call('initializeUserSession', { loginData: loginResult })
      let next = consumeOAuthNext(new URL(window.location.href).searchParams.get('next'))
      // SIGNUP-INTENT — a paid plan picked on symbols.app/built-with opens its
      // checkout once, unless the sign-in already had a destination.
      if (next === '/') next = el.call('takeSignupCheckoutNext') || next
      s.update({ loading: false })
      el.router(next, el.getRoot())
    }
  },
  tag: 'form',
  ariaBusy: (el, s) => !!s.loading,
  // ModalWindow declares `state: {}` (empty) which gives LoginWindow its
  // own isolated state. Initialize `loading` here so reactive bindings
  // (LoadingGifSection overlay + CheckButton opacity/text/cursor in
  // LoginForm/SignupForm/ResetEmailForm/ResetPasswordsForm) register the
  // dep at first render — without `loading: false` here, DOMQL's signal
  // tracker may skip the dep on undefined reads and never re-render when
  // onSubmit later sets `s.update({ loading: true })`.
  // `nativeAuthReady` — native shell only: flips true once
  // ensureNativeGoogleAuth's probe confirms the server carries the
  // deep-link exchange route; initialized here so the Google button's
  // reactive disabled/cursor/opacity factories register the dep.
  state: { loading: false, identifier: '', password: '', nativeAuthReady: false },
  // AUTH-WIPE-1 (c) — the VISIBLE STOP for the dev-infra case (Nika's third
  // condition). bootShell routes a signature-class 401 on the LOCAL channel
  // into its soft path: auth storage is KEPT and nothing is purged, but the
  // user still lands here, which is indistinguishable from an ordinary
  // sign-out. Without this notice they retype credentials that were never the
  // problem. `?authInfra=dev_signature` is set ONLY on the local channel, so
  // every other host reaches this component unchanged.
  onRender: (el, s) => {
    if (typeof window === 'undefined') return
    // Idempotency guard — onRender can fire twice in dev under HMR
    // (same guard as pages/auth/callback.js).
    if (s.authInfraNoticeShown) return
    const url = new URL(window.location.href)
    if (url.searchParams.get('authInfra') !== 'dev_signature') return
    s.update({ authInfraNoticeShown: true })
    el.call('openNotification', {
      title: el.call('polyglot', 'auth.login.devApi.title'),
      message: el.call('polyglot', 'auth.login.devApi.message'),
      type: 'error'
    })
  },
  // Bypass HTML5 native validation. The signin/signup/reset-password
  // pages all run their own field validation inside `onSubmit` and
  // surface errors via `openNotification` — letting the browser's
  // native `:invalid` interception block the submit event silently is
  // a footgun (it never reaches onSubmit, so users see nothing happen
  // when they click Sign in). Setting `novalidate` here means the
  // submit event ALWAYS fires; pages own validation end-to-end.
  novalidate: true,
  overflow: 'visible',
  position: 'relative',
  transform: 'none',
  top: 'auto',
  left: 'auto',
  flow: 'column',
  // flex-start (not space-between): with minHeight pinning the box,
  // space-between stretched the slack into a large gap between the
  // header and the form sections (2026-06-06 per Nika).
  align: 'flex-start flex-start',
  height: 'auto',
  width: 'I1',
  maxWidth: '100%',
  margin: 'auto',
  padding: 'B B2 C1',
  round: 'radiusCard',
  theme: 'common-box',
  color: 'title',
  // Border + blue glow removed 2026-06-06 per Nika ("remove
  // LoadingGifSection border and outline") — the loading overlay sits
  // inside this window, so the ring/glow read as chrome around the
  // loading gif. minHeight pins the box so the content swap during
  // sign-in doesn't resize the window.
  border: 'none',
  boxShadow: 'none',
  minHeight: 'H',
  '@mobileM': {
    padding: 'A A B',
    minHeight: 'auto',
    width: '100%',
    maxWidth: '100%'
  },
  LoadingGifSection: {
    background: 'scrim',
    position: 'absolute',
    inset: '0',
    fullSize: true,
    transition: 'B defaultBezier',
    transitionProperty: 'opacity, visibility',
    pointerEvents: (el, s) => (s.loading ? 'auto' : 'none'),
    opacity: (el, s) => (s.loading ? 1 : 0),
    visibility: (el, s) => (s.loading ? 'visible' : 'hidden')
  },
  ModalHeader: {
    width: '100%',
    margin: '0',
    Hgroup: {
      H: { tag: 'h1', fontSize: 'D', text: '{{ auth.login.heading | polyglot }}' },
      P: null
    },
    Icons: {
      // SECURITY/UX (platform#2278): the full-page /login, /signin, /signup,
      // /reset-password surfaces have NOTHING to close to — the old X did
      // `el.router('/')`, which (combined with the missing SPA auth guard, now
      // fixed in workspace/app.js) let an unauthenticated visitor walk into the
      // dashboard. There's no valid "close" on a full-page auth screen, so the
      // X is hidden by default. ONLY a modal consumer (LoginWindow rendered
      // inside an overlay, e.g. MarketplaceSigninModal) re-enables it via an
      // `Icons.Close.if`/`onClick` override that dismisses the overlay.
      Close: {
        // The brand ModalHeader's Close can't be hidden from here — neither
        // `if` nor `hide` merges through `extends: 'ModalWindow'` onto it. The
        // X is hidden on the full-page auth screens by a route-scoped CSS rule
        // in workspace/app.js (`html[data-shell-route="auth"] … Close`). What
        // DOES merge is `onClick`, so make it a safe no-op — defense in depth:
        // even if the X ever renders on a page it must NEVER navigate into the
        // app (that was the #2278 bypass — the old override did `router('/')`).
        onClick: (ev) => {
          ev?.preventDefault?.()
          ev?.stopPropagation?.()
        }
      }
    }
  },
  Flex: {
    align: 'stretch space-between',
    width: '100%',
    padding: 'B C1',
    flow: 'x',
    gap: 'C2',
    childProps: {
      flex: '1 1 0',
      minWidth: '0'
    },
    '@tabletS': {
      flow: 'column',
      gap: 'B',
      padding: 'B 0 0'
    },
    '@mobileL': {
      flow: 'column',
      wrap: 'nowrap',
      padding: 'A 0 B',
      gap: 'A',
      width: '100%',
      maxWidth: '100%'
    },
    // Keep keyboard order aligned with the visible form-first layout.
    LoginForm: null,
    SignupForm: null,
    ResetEmailForm: null,
    ResetPasswordsForm: null,
    RequestAccessButtons: {
      // Force the OAuth column to the right of the email/password form
      // even though it's declared first in this component. Pages slot
      // their LoginForm/SignupForm/etc. into Flex with default order 0,
      // and `order: 2` here pushes RequestAccessButtons after them
      // without making LoginWindow aware of which form key the page used.
      order: 2,
      height: '100%',
      padding: '0',
      background: 'transparent',
      gap: 'A2',
      Hgroup: {
        margin: '0 0 A',
        '@mobileL': { margin: '0 0 Z' },
        H: {
          tag: 'h2',
          fontSize: 'A',
          text: '{{ auth.login.alternativeHeading | polyglot }}'
        },
        P: {
          color: 'inactive',
          fontSize: 'Z2',
          text: '{{ auth.login.alternativeBody | polyglot }}'
        }
      },
      '@tabletS': {
        margin: '0',
        align: 'stretch flex-start'
      },
      '@mobileL': {
        margin: '0',
        padding: 'A 0 0',
        gap: 'A',
        border: 'none',
        borderWidth: '0',
        borderStyle: 'none',
        borderColor: 'transparent'
      },
      Flex: {
        GoogleSignInButton: {
          extends: ['Link', 'Button'],
          // type='button' — `Button` renders a native <button>, and a <button>
          // with no explicit type defaults to type=submit. Without this the
          // OAuth buttons are implicit form-submit buttons; the first one in
          // DOM order (Google) captured Enter and ran its onClick (which
          // preventDefaults), so pressing Enter in the email/password form
          // appeared to do nothing. type='button' leaves the Sign-in
          // CheckButton as the only submit button, so Enter submits the form.
          type: 'button',
          // Native shell: disabled only while the system-browser flow is
          // unconfirmed (server probe pending or 404 — s.nativeAuthReady
          // false), with the reason rendered right below
          // (P_nativeShellNotice) — never a silent no-op. Each factory
          // falls through to the exact web value when isNativeShell() is
          // false.
          disabled: (el, s) => !!s.loading || (el.call('isNativeShell') && !s.nativeAuthReady),
          margin: '0 0 0 -X',
          theme: 'quaternary',
          fontWeight: '500',
          gap: 'Z',
          cursor: (el, s) =>
            s.loading || (el.call('isNativeShell') && !s.nativeAuthReady)
              ? 'not-allowed'
              : 'pointer',
          opacity: (el, s) =>
            s.loading || (el.call('isNativeShell') && !s.nativeAuthReady) ? 0.6 : 1,
          pointerEvents: (el, s) => (s.loading ? 'none' : 'auto'),
          icon: 'googleLogoNormal',
          // CSS order flips the Icon flex child in front of the text node
          // so the brand glyph reads left of the label without reversing
          // the whole row (row-reverse would also flip Hgroup hierarchy).
          Icon: { order: '-1' },
          text: '{{ auth.login.google | polyglot }}',
          '@tabletS': {
            align: 'center center',
            fontSize: 'Z1',
            margin: '0',
            maxWidth: '100%',
            padding: 'Z1 A',
            width: '100%'
          },
          '@mobileL': {
            padding: 'Z1 -',
            align: 'center center',
            maxWidth: '100%',
            textAlign: 'center',
            border: 'none'
          },
          '@mobileM': {
            fontSize: 'Y'
          },
          // One-time GIS init. The gsi/client script is NOT loaded by
          // index.html — we lazy-inject it only on auth pages so the
          // editor's main bundle stays Google-free. `onRender` may
          // fire multiple times in dev under HMR; the `_gsiInited`
          // guard + script-tag de-dup make it idempotent.
          onRender: (el, s) => {
            if (typeof window === 'undefined') return
            // Native shell: skip ALL GIS wiring (script inject + One Tap
            // init + code client) — none of it can succeed in the embedded
            // webview (see header note). Instead arm the deep-link entry
            // paths (?xt boot param / 'symbols:auth-callback' event) and
            // feature-detect the server exchange route; the probe verdict
            // lands in s.nativeAuthReady and enables the button.
            if (el.call('isNativeShell')) {
              el.call('ensureNativeGoogleAuth')
              return
            }
            if (window._gsiInited) return
            const _GSI_SRC = 'https://accounts.google.com/gsi/client'
            if (!document.querySelector(`script[src="${_GSI_SRC}"]`)) {
              const tag = document.createElement('script')
              tag.src = _GSI_SRC
              tag.async = true
              tag.defer = true
              document.head.appendChild(tag)
            }
            const tryInit = () => {
              if (!window.google?.accounts?.id) return false
              const clientId = el.sdk?.environment?.googleClientId
              if (!clientId) {
                console.warn(
                  '[LoginWindow] no googleClientId in sdk.environment — Google button will fail'
                )
                return true // give up retrying; nothing to init
              }
              window.google.accounts.id.initialize({
                client_id: clientId,
                // WORKSPACE-AUTH-GSI-FEDCM-DEPRECATION: Google is making
                // FedCM the mandatory backend for One Tap. Opt into the
                // FedCM-driven prompt UI so we use the browser-native
                // chooser instead of Google's legacy iframe. Once this
                // is on, `PromptMomentNotification.isNotDisplayed()` /
                // `isSkippedMoment()` / `isDismissedMoment()` (and their
                // get*Reason siblings) all stop returning meaningful
                // values — we must not call them, or the GSI logger
                // re-emits the deprecation warning. The prompt() call
                // below was rewritten to drop those status checks.
                use_fedcm_for_prompt: true,
                callback: async (response) => {
                  if (!response?.credential) {
                    s.update({ loading: false })
                    return
                  }
                  const inviteToken = readInviteToken('google')
                  try {
                    const loginResult = await el.sdk.googleAuth(response.credential, inviteToken)
                    // A sign-in on this origin: its refresh token is its own (review D1).
                    el.call('markOwnSession')
                    await el.scope.completeGoogleLogin(el, s, loginResult)
                  } catch (err) {
                    console.error('[LoginWindow] googleAuth failed:', err)
                    clearInviteToken('google')
                    s.update({ loading: false })
                    el.call('openNotification', {
                      title: el.call('polyglot', 'auth.login.googleFailed'),
                      message: err?.message || el.call('polyglot', 'auth.login.tryAgain'),
                      type: 'error'
                    })
                  }
                },
                auto_select: false,
                cancel_on_tap_outside: true
              })

              // Explicit OAuth code flow — the reliable path for the button.
              // One Tap above only authenticates users ALREADY signed into
              // Google in this browser; otherwise FedCM aborts ("Not signed
              // in with the identity provider") with no recovery. initCodeClient
              // opens Google's account chooser + consent in a popup and works
              // regardless of session/FedCM state. The code is exchanged
              // server-side via /core/auth/google/callback (redirect_uri
              // 'postmessage' for the popup ux_mode).
              if (window.google.accounts.oauth2?.initCodeClient) {
                window._gsiCodeClient = window.google.accounts.oauth2.initCodeClient({
                  client_id: clientId,
                  scope: 'openid email profile',
                  ux_mode: 'popup',
                  callback: async (resp) => {
                    if (!resp?.code) {
                      s.update({ loading: false })
                      return
                    }
                    const inviteToken = readInviteToken('google')
                    try {
                      const loginResult = await el.sdk.googleAuthCallback(
                        resp.code,
                        'postmessage',
                        inviteToken
                      )
                      // A sign-in on this origin: its refresh token is its own (review D1).
                      el.call('markOwnSession')
                      await el.scope.completeGoogleLogin(el, s, loginResult)
                    } catch (err) {
                      console.error('[LoginWindow] googleAuthCallback failed:', err)
                      clearInviteToken('google')
                      s.update({ loading: false })
                      el.call('openNotification', {
                        title: el.call('polyglot', 'auth.login.googleFailed'),
                        message: err?.message || el.call('polyglot', 'auth.login.tryAgain'),
                        type: 'error'
                      })
                    }
                  }
                })
              }
              window._gsiInited = true
              return true
            }
            if (!tryInit()) {
              // gsi/client still loading — retry briefly
              const t = setInterval(() => {
                if (tryInit()) clearInterval(t)
              }, 200)
              setTimeout(() => clearInterval(t), 5000)
            }
          },
          onClick: (ev, el, s) => {
            ev?.preventDefault?.()
            // Native shell: run OAuth in the SYSTEM browser (Safari) with
            // the symbols:// deep link back. Gated on the server probe —
            // when the exchange route isn't deployed the button stays
            // disabled, and this branch keeps the reason as a
            // defense-in-depth notification (in case a webview ever drops
            // the disabled attr), never a silent no-op.
            if (el.call('isNativeShell')) {
              if (!s.nativeAuthReady) {
                el.call('openNotification', {
                  title: el.call('polyglot', 'auth.login.googleNotInApp.title'),
                  message: el.call('polyglot', 'auth.login.googleNotInApp.message'),
                  type: 'warning'
                })
                return
              }
              // Persist invite token + post-OAuth next across the Safari
              // round-trip (same stashes as the web path below).
              stashInviteToken('google')
              stashOAuthNext()
              s.update({ loading: true })
              el.call('launchNativeGoogleAuth')
              // No cancel signal if the user abandons Safari — cap the
              // spinner like the web popup path (the deep-link completion
              // owns `loading` from the moment it fires).
              setTimeout(() => {
                if (s.loading) s.update({ loading: false })
              }, 60000)
              return
            }
            if (!window.google?.accounts?.id) {
              el.call('openNotification', {
                title: el.call('polyglot', 'auth.login.googleUnavailable.title'),
                message: el.call('polyglot', 'auth.login.googleUnavailable.message'),
                type: 'error'
              })
              return
            }
            // Persist invite token (?token=…) + post-OAuth next so they
            // survive the prompt round-trip even when the credential
            // callback fires from a different invocation.
            stashInviteToken('google')
            stashOAuthNext()
            s.update({ loading: true })
            try {
              // Explicit OAuth popup (Google account chooser + consent) —
              // works for every user regardless of whether they're already
              // signed into Google or whether FedCM is available. This is the
              // reliable replacement for the One Tap `prompt()`, which aborted
              // ("Not signed in with the identity provider") for anyone without
              // an active Google session. The code-client callback resets
              // `loading` on success; if the user closes the popup we get no
              // signal, so cap the spinner with a 30s safety timeout.
              if (window._gsiCodeClient) {
                window._gsiCodeClient.requestCode()
              } else {
                // Code client not ready yet (gsi/client still loading) — fall
                // back to One Tap so the click isn't a no-op.
                window.google.accounts.id.prompt(() => {})
              }
              setTimeout(() => {
                if (s.loading) s.update({ loading: false })
              }, 30000)
            } catch (err) {
              console.error('[LoginWindow] One Tap prompt failed:', err)
              s.update({ loading: false })
              el.call('openNotification', {
                title: el.call('polyglot', 'auth.login.googleSignInFailed'),
                message: err?.message || el.call('polyglot', 'auth.login.tryAgain'),
                type: 'error'
              })
            }
          }
        },
        // Native shell only: the visible reason the Google button above is
        // disabled — inline text, not a title tooltip (hover doesn't exist
        // on touch). Hidden reactively once the system-browser flow is
        // confirmed (s.nativeAuthReady) — the guard survives only as the
        // fallback for servers that predate the exchange route.
        // `_`-suffix key auto-extends the built-in P.
        P_nativeShellNotice: {
          if: (el) => el.call('isNativeShell'),
          display: (el, s) => (s.nativeAuthReady ? 'none' : 'block'),
          text: '{{ auth.login.googleNativeHint | polyglot }}',
          color: 'caption',
          fontSize: 'Y',
          margin: '0',
          maxWidth: '100%'
        },
        GithubSignInButton: {
          // Hide Github on preview embeds — the OAuth callback hops the
          // top frame and breaks the embedded preview UX.
          if: (el) => {
            const env = el.sdk?.environment
            return !env?.isPreview
          },
          extends: ['Link', 'Button'],
          // type='button' — `Button` renders a native <button>, and a <button>
          // with no explicit type defaults to type=submit. Without this the
          // OAuth buttons are implicit form-submit buttons; the first one in
          // DOM order (Google) captured Enter and ran its onClick (which
          // preventDefaults), so pressing Enter in the email/password form
          // appeared to do nothing. type='button' leaves the Sign-in
          // CheckButton as the only submit button, so Enter submits the form.
          type: 'button',
          disabled: (el, s) => !!s.loading,
          margin: '0 0 0 -X',
          theme: 'quaternary',
          fontWeight: '500',
          gap: 'Z',
          cursor: (el, s) => (s.loading ? 'not-allowed' : 'pointer'),
          opacity: (el, s) => (s.loading ? 0.6 : 1),
          pointerEvents: (el, s) => (s.loading ? 'none' : 'auto'),
          icon: 'github',
          Icon: { order: '-1' },
          text: '{{ auth.login.github | polyglot }}',
          '@tabletS': {
            align: 'center center',
            fontSize: 'Z1',
            margin: '0',
            maxWidth: '100%',
            padding: 'Z1 A',
            width: '100%'
          },
          '@mobileL': {
            padding: 'Z1 -',
            align: 'center center',
            maxWidth: '100%',
            textAlign: 'center',
            border: 'none'
          },
          '@mobileM': {
            fontSize: 'Y'
          },
          onClick: (ev, el, s) => {
            ev?.preventDefault?.()
            const env = el.sdk?.environment
            if (!env?.githubClientId) {
              el.call('openNotification', {
                title: el.call('polyglot', 'auth.login.githubUnavailable.title'),
                message: el.call('polyglot', 'auth.login.githubUnavailable.message'),
                type: 'error'
              })
              return
            }
            s.update({ loading: true })
            try {
              stashInviteToken('github')
              stashOAuthNext()
              const redirectUri = `${window.location.origin}/github-auth-callback`
              const scope = 'user:email'
              window.location.href =
                `https://github.com/login/oauth/authorize` +
                `?client_id=${encodeURIComponent(env.githubClientId)}` +
                `&redirect_uri=${encodeURIComponent(redirectUri)}` +
                `&scope=${encodeURIComponent(scope)}`
            } catch (err) {
              console.error('[LoginWindow] GitHub redirect failed:', err)
              clearInviteToken('github')
              s.update({ loading: false })
              el.call('openNotification', {
                title: el.call('polyglot', 'auth.login.githubStartFailed'),
                message: err?.message || el.call('polyglot', 'auth.login.tryAgain'),
                type: 'error'
              })
            }
          }
        },
        Link: null
      }
    }
  }
}
